import { useEffect, useRef, useState } from 'react'
import { Textarea } from 'react-daisyui'

interface AutoSaveTextareaProps {
  value: string
  placeholder: string
  onSave: (val: string) => void
  /** Minimum height in rows (also the height while empty when `autoGrow` is set). */
  rows?: number
  /** Grow with the content instead of staying at a fixed height. */
  autoGrow?: boolean
  className?: string
}

/** Textarea that saves 1s after the last keystroke — and immediately on blur. */
export default function AutoSaveTextarea({ value, placeholder, onSave, rows = 3, autoGrow, className }: AutoSaveTextareaProps) {
  const [localValue, setLocalValue] = useState(value)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const savedRef = useRef(value)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Height follows the content — 'auto' first so it can shrink again as well.
  useEffect(() => {
    if (!autoGrow) return
    const element = textareaRef.current
    if (!element) return
    element.style.height = 'auto'
    element.style.height = `${element.scrollHeight}px`
  }, [autoGrow, localValue])

  useEffect(() => {
    setLocalValue(value)
    savedRef.current = value
  }, [value])

  function handleChange(newValue: string) {
    setLocalValue(newValue)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      if (newValue !== savedRef.current) {
        savedRef.current = newValue
        onSave(newValue)
      }
    }, 1000)
  }

  // Also save on blur immediately
  function handleBlur() {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (localValue !== savedRef.current) {
      savedRef.current = localValue
      onSave(localValue)
    }
  }

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  return (
    <Textarea
      ref={textareaRef}
      className={`w-full ${autoGrow ? 'resize-none overflow-hidden' : ''} ${className || ''}`}
      rows={rows}
      placeholder={placeholder}
      value={localValue}
      onChange={e => handleChange(e.target.value)}
      onBlur={handleBlur}
    />
  )
}
